import { primaryAddress } from "@productlane/lib/src/email-settings"
import { Alert, Button } from "@productlane/ui"

import type { EmailInboxSettingsProps } from "../types"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../settings-switches/settings-container"
import { DnsRecordBlock } from "../dns-record-block"

export function DnsStep({ workspace }: EmailInboxSettingsProps) {
	const utils = api.useUtils()
	const rootDomain = primaryAddress(workspace.emailSettings)
	const verifyMutation = api.mail.verifyEmailDomain.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			setTimeout(() => {
				const settingsScrollContainer = document.getElementById(
					"settings-scroll-container",
				)
				if (settingsScrollContainer) {
					settingsScrollContainer.scrollTo({
						top: settingsScrollContainer.scrollHeight,
						behavior: "smooth",
					})
				}
			}, 100)
		},
		onError: (error) => {
			toast.error({
				title: "Unable to verify DNS records",
				description: error.message,
			})
		},
	})

	return (
		<>
			<div className="mb-2 font-semibold text-primary">Sending emails</div>
			<div className="mb-4 text-sm text-secondary">
				To enable Productlane to send emails on your behalf, you will need to
				verify your domain. This will ensure that email clients recognize these
				emails as being sent with your permission.
			</div>
			<SettingsContainer className="space-y-3 divide-y-0">
				<div className="mb-2 text-sm font-medium">
					Add these DNS records to your domain {rootDomain?.domain}.
				</div>

				<DnsRecordBlock
					type="TXT"
					recordKey={rootDomain?.txtDnsKey ?? ""}
					value={rootDomain?.txtDnsValue ?? ""}
					isActive={!!rootDomain?.isTxtDnsActive}
				/>

				<DnsRecordBlock
					type="CNAME"
					recordKey={rootDomain?.cnameDnsKey ?? ""}
					value={rootDomain?.cnameDnsValue ?? ""}
					isActive={!!rootDomain?.isCnameDnsActive}
				/>

				<Alert header="Note about Cloudflare">
					If you use Cloudflare, you need to disable the Proxy for this CNAME
					record.
				</Alert>

				<Alert header="DNS Setup & Propagation" variant="info" className="mt-4">
					To fully enable email functionality, you must complete the DNS setup
					correctly. Please note that DNS changes may take up to 48 hours to
					propagate globally.
				</Alert>

				<div className="mt-4 flex justify-end">
					<Button
						isLoading={verifyMutation.isLoading}
						disabled={!rootDomain}
						onClick={() => {
							if (rootDomain) verifyMutation.mutate({ id: rootDomain.id })
						}}
					>
						Verify DNS records
					</Button>
				</div>
			</SettingsContainer>
		</>
	)
}
